import { useState } from 'react';
import Papa from 'papaparse';
import { supabase } from './supabase';
import appConfig from './appConfig';
import { cleanCity } from './cleanCity'; // PATCH oct21-ui-fix2

// Session 31: plan limits (same as App.js)
const CUSTOMER_LIMITS = { starter: 200, pro: 500, business: Infinity };

// PATCH oct21-ui-csv: teal look like the sign in screens, dark mode, line icons
function csvColors() {
  var dark = false;
  try { dark = localStorage.getItem('darkMode') === 'true'; } catch (e) {}
  return {
    teal: '#2F6F6B',
    accent: dark ? '#7CC2BB' : '#2F6F6B',
    tint: dark ? '#1C2E2C' : '#E6F0EF',
    card: dark ? '#1A201F' : '#FFFFFF',
    section: dark ? '#161B1A' : '#F7F9F8',
    text: dark ? '#E8EEED' : '#1C2B2A',
    sub: dark ? '#B8C2C1' : '#3A4645',
    muted: dark ? '#8E9998' : '#5F6B6A',
    border: dark ? '#2C3433' : '#E2E7E6',
    cardBorder: dark ? '#2C3433' : '#D5DCDB',
    error: dark ? '#F0776B' : '#B42318',
    errorBg: dark ? '#3A1F1D' : '#FBEAE8',
    success: dark ? '#5DB683' : '#2F7D4F',
    successBg: dark ? '#16301F' : '#E5F4EA',
    disabled: dark ? '#3A4443' : '#CBD3D2',
  };
}

var CSV_ICONS = {
  arrowLeft: ['M19 12H5', 'M12 19l-7-7 7-7'],
  fileText: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6', 'M16 13H8', 'M16 17H8'],
  chevronDown: ['M6 9l6 6 6-6'],
  chevronUp: ['M18 15l-6-6-6 6'],
  phone: ['M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z', 'M12 18h.01'],
  info: [{ c: [12, 12, 9] }, 'M12 16v-4', 'M12 8h.01'],
  check: ['M20 6L9 17l-5-5'],
  alert: [{ c: [12, 12, 9] }, 'M12 8v4', 'M12 16h.01'],
  upload: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M17 8l-5-5-5 5', 'M12 3v12'],
};
function CsvIcon({ name, size }) {
  var parts = CSV_ICONS[name] || [];
  var s = size || 18;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block', flexShrink: 0 }}>
      {parts.map(function (p, i) { return typeof p === 'string' ? <path key={i} d={p} /> : <circle key={i} cx={p.c[0]} cy={p.c[1]} r={p.c[2]} />; })}
    </svg>
  );
}

export default function CSVImport({ onClose, plan }) {
  const [preview, setPreview] = useState([]);
  const [allData, setAllData] = useState([]);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [skipped, setSkipped] = useState(0);
  const [fileName, setFileName] = useState(''); // PATCH oct21-ui-csv

  const handleFile = (e) => {
    Papa.parse(e.target.files[0], {
      header: true,
      complete: (results) => {
        var cleaned = results.data.filter(function(row) {
          return (row['First Name'] || row['Last Name']) && row['Phone 1 - Value'];
        });
        setAllData(cleaned);
        setPreview(cleaned.slice(0, 5));
        setStatus(cleaned.length + ' contacts ready to import');
        setDone(false);
        setProgress(0);
        setSkipped(0);
      }
    });
  };

  const runImport = async () => {
    setImporting(true);
    setDone(false);

    // Get current user session for assigned_to
    var { data: sessionData } = await supabase.auth.getSession();
    var userId = sessionData?.session?.user?.id || null;
    var companyId = appConfig.companyId;

    if (!companyId) {
      setStatus('Error: Company ID not found. Please sign out and sign in again.');
      setImporting(false);
      return;
    }

    // Fetch existing phones to avoid duplicates.
    // Session 31: paged (one request returns max 1000 rows, so duplicates past 1000 were missed).
    var existing = [];
    var from = 0;
    while (true) {
      var { data: page, error: pageErr } = await supabase
        .from('customers')
        .select('phone')
        .eq('company_id', companyId)
        .order('id', { ascending: true })
        .range(from, from + 999);
      if (pageErr) {
        setStatus('Error: could not check existing customers. Please try again.');
        setImporting(false);
        return;
      }
      if (!page || page.length === 0) break;
      existing.push(...page);
      if (page.length < 1000) break;
      from += 1000;
    }

    // Session 31: plan customer limit
    var limit = CUSTOMER_LIMITS[plan];
    var room = (!limit || limit === Infinity) ? Infinity : Math.max(0, limit - existing.length);
    if (room <= 0) {
      setStatus('Customer limit reached (' + limit + ' on your plan). No contacts were imported.');
      setImporting(false);
      return;
    }
    var added = 0;
    var overLimit = 0;
    var existingPhones = new Set(
      (existing || []).map(c => c.phone ? c.phone.replace(/\D/g, '').slice(-10) : '')
    );

    var batchSize = 50;
    var imported = 0;
    var skippedCount = 0;
    var totalToProcess = allData.length;

    for (var i = 0; i < allData.length; i += batchSize) {
      var batch = allData.slice(i, i + batchSize).map(function(row) {
        var firstName = row['First Name'] || '';
        var middleName = row['Middle Name'] || '';
        var lastName = row['Last Name'] || '';
        var fullName = [firstName, middleName, lastName].filter(Boolean).join(' ').trim();
        var phone = (row['Phone 1 - Value'] || '').replace(/\D/g, '');
        var email = row['E-mail 1 - Value'] || '';
        var street = row['Address 1 - Street'] || '';
        var city = row['Address 1 - City'] || '';
        var address = [street, city].filter(Boolean).join(', ');
        var notes = row['Notes'] || '';
        var normalizedPhone = phone.slice(-10);

        // Skip duplicates
        if (existingPhones.has(normalizedPhone)) {
          skippedCount++;
          return null;
        }
        // Session 31: stop at the plan limit
        if (added >= room) {
          overLimit++;
          return null;
        }
        added++;
        existingPhones.add(normalizedPhone);

        return {
          full_name: fullName,
          phone: phone,
          email: email,
          address: address,
          area: cleanCity(city),
          notes: notes,
          customer_type: 'lead',
          company_id: companyId,       // ✅ FIXED — was missing
          assigned_to: userId,          // ✅ NEW — contact privacy
          bulk_imported: true,          // ✅ NEW — excluded from New Leads count
        };
      }).filter(Boolean); // remove nulls (duplicates)

      if (batch.length > 0) {
        const { error } = await supabase.from('customers').insert(batch); // lint-ignore-company-id — batch rows already include company_id, built above
        if (error) {
          console.error('Import error:', error);
          setStatus('Error: ' + error.message);
          setImporting(false);
          return;
        }
        imported += batch.length;
      }

      setProgress(Math.round(((i + batchSize) / totalToProcess) * 100));
    }

    setSkipped(skippedCount);
    setStatus('Done! Imported ' + imported + ' contacts' + (skippedCount > 0 ? ', skipped ' + skippedCount + ' duplicates.' : '.') + (overLimit > 0 ? ' ' + overLimit + ' not imported: plan limit of ' + limit + ' customers reached.' : ''));
    setImporting(false);
    setDone(true);
  };

  // PATCH oct21-ui-csv: screen layout
  var C = csvColors();
  var card = { background: C.card, border: '1px solid ' + C.cardBorder, borderRadius: 14, padding: 16, marginBottom: 16, boxSizing: 'border-box' };
  var stepTitle = { margin: '0 0 6px', fontWeight: 600, fontSize: 14, color: C.text };
  var stepLine = { margin: '0 0 4px', fontSize: 14, color: C.sub, lineHeight: 1.5 };
  var deviceBox = { display: 'flex', gap: 10, alignItems: 'flex-start', background: C.card, border: '1px solid ' + C.border, borderRadius: 10, padding: 12, marginBottom: 8 };
  var isError = status.indexOf('Error') === 0 || status.indexOf('Customer limit') === 0;
  var statusFg = isError ? C.error : done ? C.success : C.accent;
  var statusBg = isError ? C.errorBg : done ? C.successBg : C.tint;
  var pct = Math.min(progress, 100);

  return (
    <div style={{ fontFamily: 'inherit', maxWidth: 600, margin: '0 auto', padding: 16, color: C.text, boxSizing: 'border-box' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <button onClick={onClose} aria-label="Back"
          style={{ minHeight: 44, padding: '0 12px 0 8px', background: 'transparent', color: C.sub, border: 'none', borderRadius: 10,
            cursor: 'pointer', fontSize: 15, fontWeight: 600, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
          <CsvIcon name="arrowLeft" size={18} /> Back
        </button>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>Import contacts</h2>
      </div>

      {/* Step by step guide */}
      <div style={{ ...card, background: C.tint, border: '1px solid ' + C.accent }}>
        <button onClick={function() { setShowGuide(!showGuide); }} aria-expanded={showGuide}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', padding: 0,
            cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', color: C.accent, minHeight: 32 }}>
          <CsvIcon name="fileText" size={20} />
          <span style={{ flex: 1, fontWeight: 600, fontSize: 15 }}>How to export contacts from Google</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 600 }}>
            {showGuide ? 'Hide' : 'Show'} <CsvIcon name={showGuide ? 'chevronUp' : 'chevronDown'} size={16} />
          </span>
        </button>
        {showGuide && (
          <div style={{ marginTop: 14 }}>
            <p style={stepTitle}>Step 1: Export from Google Contacts</p>
            <p style={stepLine}>1. Go to <a href="https://contacts.google.com" target="_blank" rel="noopener noreferrer" style={{ color: C.accent, fontWeight: 600 }}>contacts.google.com</a></p>
            <p style={stepLine}>2. Click <strong>Export</strong> (left sidebar)</p>
            <p style={stepLine}>3. Select <strong>Google CSV</strong></p>
            <p style={{ ...stepLine, marginBottom: 16 }}>4. Click <strong>Export</strong>. The file downloads automatically</p>

            <p style={stepTitle}>Step 2: Find the file on your device</p>
            <div style={deviceBox}>
              <span style={{ color: C.accent, marginTop: 1 }}><CsvIcon name="phone" size={18} /></span>
              <div>
                <p style={{ margin: '0 0 2px', fontWeight: 600, fontSize: 14, color: C.text }}>Android</p>
                <p style={{ margin: '0 0 2px', color: C.sub, fontSize: 13 }}>Internal storage › Downloads › contacts.csv</p>
                <p style={{ margin: 0, color: C.muted, fontSize: 13 }}>Open the Files app, then the Downloads folder</p>
              </div>
            </div>
            <div style={{ ...deviceBox, marginBottom: 0 }}>
              <span style={{ color: C.accent, marginTop: 1 }}><CsvIcon name="phone" size={18} /></span>
              <div>
                <p style={{ margin: '0 0 2px', fontWeight: 600, fontSize: 14, color: C.text }}>iPhone</p>
                <p style={{ margin: '0 0 2px', color: C.sub, fontSize: 13 }}>Files › Downloads › contacts.csv</p>
                <p style={{ margin: 0, color: C.muted, fontSize: 13 }}>Open the Files app, then Browse, then Downloads</p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 12, color: C.muted, fontSize: 13, lineHeight: 1.45 }}>
              <span style={{ marginTop: 1 }}><CsvIcon name="info" size={16} /></span>
              <span>Tip: export on a computer. If you export from your phone, the file will be in your Google Drive or email.</span>
            </div>
          </div>
        )}
      </div>

      {/* File picker */}
      <div style={{ ...card, position: 'relative' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8 }}>Google CSV contacts file</div>
        <label htmlFor="csv_file"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 46, borderRadius: 10,
            border: '1.5px dashed ' + C.accent, background: C.section, color: C.accent, fontSize: 15, fontWeight: 600,
            cursor: 'pointer', padding: '0 12px', boxSizing: 'border-box', textAlign: 'center' }}>
          <CsvIcon name="upload" size={18} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileName ? fileName : 'Choose CSV file'}</span>
        </label>
        <input id="csv_file" type="file" accept=".csv"
          onChange={function(e) { setFileName(e.target.files && e.target.files[0] ? e.target.files[0].name : ''); handleFile(e); }}
          style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden' }} />
        {fileName ? <div style={{ fontSize: 13, color: C.muted, marginTop: 8 }}>Tap to choose a different file</div> : null}
      </div>

      {status ? (
        <div role={isError ? 'alert' : 'status'}
          style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: statusBg, color: statusFg, borderRadius: 10,
            padding: '10px 12px', fontSize: 14, fontWeight: 600, lineHeight: 1.45, marginBottom: 16 }}>
          <span style={{ marginTop: 1 }}><CsvIcon name={isError ? 'alert' : done ? 'check' : 'info'} size={18} /></span>
          <span>{status}</span>
        </div>
      ) : null}

      {progress > 0 ? (
        <div style={{ marginBottom: 16 }}>
          <div style={{ background: C.border, borderRadius: 99, height: 10, overflow: 'hidden' }}>
            <div style={{ background: C.teal, height: '100%', width: pct + '%', borderRadius: 99, transition: 'width 0.3s' }}/>
          </div>
          <p style={{ textAlign: 'center', fontSize: 13, color: C.muted, margin: '6px 0 0' }}>{pct}%</p>
        </div>
      ) : null}

      {preview.length > 0 ? (
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: C.text, margin: '0 0 8px' }}>Preview (first 5 contacts)</h3>
          <div style={{ background: C.card, border: '1px solid ' + C.cardBorder, borderRadius: 14, overflow: 'hidden', marginBottom: 16 }}>
            {preview.map(function(row, i) {
              var firstName = row['First Name'] || '';
              var middleName = row['Middle Name'] || '';
              var lastName = row['Last Name'] || '';
              var fullName = [firstName, middleName, lastName].filter(Boolean).join(' ').trim();
              var phone = row['Phone 1 - Value'] || '';
              var city = row['Address 1 - City'] || '';
              return (
                <div key={i} style={{ padding: '12px 16px', borderBottom: i < preview.length - 1 ? '1px solid ' + C.border : 'none', fontSize: 14 }}>
                  <div style={{ fontWeight: 600, color: C.text }}>{fullName}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px', marginTop: 2 }}>
                    <span style={{ color: C.muted }}>{phone}</span>
                    {city ? <span style={{ color: C.accent }}>{city}</span> : null}
                  </div>
                </div>
              );
            })}
          </div>

          {!done && (
            <button onClick={runImport} disabled={importing}
              style={{
                width: '100%', minHeight: 48, padding: '0 16px',
                background: importing ? C.disabled : C.teal,
                color: 'white', border: 'none', borderRadius: 10, fontSize: 16, fontFamily: 'inherit',
                cursor: importing ? 'not-allowed' : 'pointer', fontWeight: 600,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
              }}>
              {importing ? 'Importing...' : 'Import all ' + allData.length + ' contacts'}
            </button>
          )}

          {done && (
            <div role="status" style={{
              width: '100%', minHeight: 48, padding: '0 16px', background: C.successBg, boxSizing: 'border-box',
              border: '1.5px solid ' + C.success, borderRadius: 10, fontSize: 16,
              fontWeight: 600, color: C.success, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
            }}>
              <CsvIcon name="check" size={20} /> Import complete
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
